import PendingApprovalsNotice from "../components/PendingApprovalsNotice";
import AssociationChange from '../components/AssociationChange';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCurrentUsername, roleLabel } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;
const headers = () => ({ token: localStorage.getItem('token') });
const statusLabels = { pending: 'Väntar på godkännande', approved: 'Godkänd', rejected: 'Avvisad', suspended: 'Avstängd' };

function AssociationAccounts({ association, userRole, revision, onChanged, search = '' }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [expandedRoles, setExpandedRoles] = useState(search ? { 1: true, 2: true, 3: true, 5: true } : {});
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    let active = true;
    setUsers(null);
    setError('');
    axios.get(`${API_URL}/users/directory/${association._id}`, { headers: headers(), params: { q: search } })
      .then(({ data }) => { if (active) setUsers(data.users); })
      .catch(() => { if (active) setError('Kunde inte hämta föreningens konton.'); });
    return () => { active = false; };
  }, [open, association._id, revision, attempt, search]);

  function userCard(user) {
    const canEdit = userRole === 1 && user.username !== getCurrentUsername();
    return (
      <div key={user._id} className="theme-panel-muted">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <p className="break-words font-semibold text-slate-800">{user.name || user.email || 'Okänd användare'}</p>
            <p className="mt-1 break-all text-sm text-slate-600">{user.email || 'Ingen e-postadress'}</p>
            <p className="mt-1 text-sm text-slate-600">{roleLabel(user.role)} · {statusLabels[user.approvalStatus] || 'Okänd status'}</p>
            <p className="mt-1 text-xs text-slate-600">{user.emailVerifiedAt ? 'E-post verifierad' : 'E-post ej verifierad'}</p>
          </div>
          {canEdit && <AssociationChange user={user} onChanged={onChanged} />}
          {canEdit && <div className="flex flex-wrap gap-2">
            <button className="theme-badge-button border-sky-200 text-sky-700" onClick={() => navigate('/modify-user', { state: { username: user.username } })}>Ändra</button>
            <button className="theme-badge-button border-red-200 text-red-700" onClick={() => navigate('/delete-user', { state: { username: user.username } })}>Ta bort</button>
          </div>}
        </div>
      </div>
    );
  }

  return (
    <section className="theme-panel">
      <h2>
        <button className="flex w-full items-center justify-between gap-3 text-left font-semibold text-slate-800" aria-expanded={open} aria-controls={`association-${association._id}`} onClick={() => setOpen(!open)}>
          <span className="break-words">{association.name}{association.active === false ? ' (inaktiv)' : ''}</span>
          <span className="shrink-0 text-sm">{search ? `${association.matchCount} ${association.matchCount === 1 ? 'träff' : 'träffar'} av ${association.count} konton` : `${association.count} konton`} <span aria-hidden="true">{open ? '▾' : '▸'}</span></span>
        </button>
      </h2>
      {open && <div id={`association-${association._id}`} className="mt-4 space-y-4">
        {error ? <div className="theme-alert-error" role="alert">{error} <button className="underline" onClick={() => setAttempt(attempt + 1)}>Försök igen</button></div>
          : users === null ? <p role="status">Hämtar konton…</p>
          : <>
            {users.length === 0 && <p className="text-sm text-slate-600">{search ? 'Inga matchande konton i föreningen.' : 'Inga registrerade konton i föreningen.'}</p>}
            <h3 className="font-semibold text-slate-800">Föreningsadmin ({users.filter(user => user.role === 4).length})</h3>
            {users.filter(user => user.role === 4).map(userCard)}
            {!users.some(user => user.role === 4) && <p className="text-sm text-slate-600">{search ? 'Ingen föreningsadmin matchar sökningen.' : 'Ingen föreningsadmin registrerad.'}</p>}
            {[5, 3, 1, 2].map(role => {
              const members = users.filter(user => user.role === role);
              if ((search || [1, 2].includes(role)) && !members.length) return null;
              return <section key={role} className="border-t border-slate-200 pt-3">
                <h3><button className="flex w-full justify-between text-left font-semibold text-slate-800" aria-expanded={Boolean(expandedRoles[role])} aria-controls={`association-${association._id}-role-${role}`} onClick={() => setExpandedRoles(current => ({ ...current, [role]: !current[role] }))}>
                  <span>{roleLabel(role)} ({members.length})</span><span aria-hidden="true">{expandedRoles[role] ? '▾' : '▸'}</span>
                </button></h3>
                {expandedRoles[role] && <div id={`association-${association._id}-role-${role}`} className="mt-3 space-y-3">
                  {members.map(userCard)}
                  {!members.length && <p className="text-sm text-slate-600">Inga konton med denna roll.</p>}
                </div>}
              </section>;
            })}
          </>}
      </div>}
    </section>
  );
}

export default function UserManagement({ userRole }) {
  const [associations, setAssociations] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [revision, setRevision] = useState(0);
  const globalAdmin = [1, 2].includes(userRole);
  const query = search.trim();
  const [loadedSearch, setLoadedSearch] = useState(null);

  useEffect(() => {
    let active = true;
    setError('');
    setLoadedSearch(null);
    const timer = window.setTimeout(() => {
      axios.get(`${API_URL}/users/directory`, { headers: headers(), params: { q: query } })
        .then(({ data }) => {
          if (active) { setAssociations(data.associations); setLoadedSearch(query); }
        })
        .catch(() => { if (active) setError('Kunde inte hämta sökresultatet.'); });
    }, query ? 250 : 0);
    return () => { active = false; window.clearTimeout(timer); };
  }, [revision, query]);

  const searching = loadedSearch !== query;
  const visible = searching ? [] : [...(associations || [])].sort((a, b) => a.name.localeCompare(b.name, 'sv'));

  return (
    <div className="mx-auto w-full max-w-5xl px-3 py-4">
      <div className="theme-card">
        <div className="flex flex-col justify-between gap-3 sm:flex-row">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">{globalAdmin ? 'Administration' : 'Min förening'}</p>
            <h1 className="mt-2 text-lg font-bold text-slate-800">{globalAdmin ? 'Användarhantering' : 'Min förenings konton'}</h1>
            <p className="mt-2 text-sm text-slate-600">Öppna en förening för att se föreningsadmin. Öppna sedan Tränare eller Utövare för att se kontona.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {globalAdmin && <Link className="theme-button-primary" to="/account-approvals">Kontogodkännande</Link>}
            {userRole === 1 && <Link className="theme-button-secondary" to="/association-import">Importera föreningar</Link>}
            {globalAdmin && <Link className="theme-button-primary" to="/reg-user">Lägg till användare</Link>}
            <Link className="theme-button-secondary" to={globalAdmin ? '/admin-dashboard' : '/mypage'}>{globalAdmin ? 'Till administration' : 'Till min sida'}</Link>
          </div>
        </div>
        <PendingApprovalsNotice userRole={userRole} />
        <label className="mt-5 block text-sm font-semibold text-slate-800">Sök förening eller användare
          <input type="search" className="theme-input mt-2 w-full" value={search} onChange={event => setSearch(event.target.value)} maxLength={150} placeholder="Förening, namn eller e-postadress" />
        </label>
        {error && <div role="alert" className="theme-alert-error mt-4">{error} <button className="underline" onClick={() => setRevision(value => value + 1)}>Försök igen</button></div>}
        {searching && !error && <p role="status" className="mt-4">{query ? 'Söker föreningar och användare…' : 'Hämtar föreningar…'}</p>}
        {!searching && !error && !visible.length && <p className="theme-empty-state mt-4">{query ? 'Inga föreningar eller användare matchar sökningen.' : 'Inga föreningar att visa.'}</p>}
        <div className="mt-5 space-y-4">
          {visible.map(association => <AssociationAccounts key={`${association._id}:${query}`} association={association} search={association.associationMatch ? '' : query} userRole={userRole} revision={revision} onChanged={() => setRevision(value => value + 1)} />)}
        </div>
      </div>
    </div>
  );
}
